
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, TextField, Button, Typography, Box, Alert,
  FormControl, InputLabel, Select, MenuItem, Grid, InputAdornment,
  Divider, Chip, Stack, Avatar
} from '@mui/material';
import {
  CloudUpload, AutoAwesome, CheckCircle, Category, AttachMoney,
  LocationOn, Description, Inventory2, ArrowForward
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { inventoryAPI, aiAPI } from '../services/api';

const MotionPaper = motion(Paper);

const categories = [
  'Bearings', 'Motors', 'Pumps', 'Valves', 'Couplings',
  'Electrical', 'Hydraulic', 'Pneumatic', 'Tools', 'Raw Materials', 'Other'
];

const UploadInventoryPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    item_name: '', category: 'Bearings', description: '', specifications: '',
    quantity: 1, original_price: '', selling_price: '', condition: 'good',
    location: '', manufacturing_date: '', brand: '', model_number: '',
    warranty_months: '', weight_kg: '', min_order_quantity: 1,
  });
  const [error, setError] = useState('');
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleAnalyze = async () => {
    if (!formData.item_name || !formData.original_price) {
      toast.error('Enter item name and original price first');
      return;
    }
    setAnalyzing(true);
    try {
      const response = await aiAPI.analyze({
        item_name: formData.item_name,
        quantity: parseInt(formData.quantity) || 1,
        original_price: parseFloat(formData.original_price),
        condition: formData.condition,
        age_years: 2,
      });
      setAiSuggestion(response.data);
      if (response.data.suggested_price) {
        setFormData(prev => ({ ...prev, selling_price: response.data.suggested_price.toString() }));
      }
      toast.success('AI analysis complete');
    } catch (err) {
      toast.error('Analysis failed. Set price manually.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = {
        ...formData,
        quantity: parseInt(formData.quantity),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        selling_price: parseFloat(formData.selling_price),
        min_order_quantity: parseInt(formData.min_order_quantity) || 1,
        warranty_months: formData.warranty_months ? parseInt(formData.warranty_months) : null,
        weight_kg: formData.weight_kg ? parseFloat(formData.weight_kg) : null,
      };
      const response = await inventoryAPI.create(payload);
      try { await aiAPI.indexItem(response.data.id); } catch (e) {}
      toast.success('Item uploaded successfully!');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '80vh', py: 5 }}>
      <Container maxWidth="md">
        <MotionPaper
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, border: '1px solid #f1f5f9' }}
          elevation={0}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Avatar sx={{
              width: 48, height: 48, background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
              boxShadow: '0 6px 20px rgba(37,99,235,0.3)',
            }}>
              <CloudUpload sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>Upload Inventory</Typography>
              <Typography variant="body2" color="text.secondary">
                List your surplus items and AI will find buyers
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            {/* Basic Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Category sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary">BASIC INFORMATION</Typography>
            </Box>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth label="Item Name" required value={formData.item_name}
                  onChange={handleChange('item_name')} placeholder="e.g., SKF Bearing 6205-2RS" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select value={formData.category} label="Category" onChange={handleChange('category')}>
                    {categories.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Brand" value={formData.brand}
                  onChange={handleChange('brand')} placeholder="e.g., SKF, Bosch, Siemens" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Model Number" value={formData.model_number}
                  onChange={handleChange('model_number')} placeholder="e.g., 6205-2RS" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Description" multiline rows={3}
                  value={formData.description} onChange={handleChange('description')}
                  placeholder="Describe the item, its condition, and usage history..." />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Technical Specifications" multiline rows={2}
                  value={formData.specifications} onChange={handleChange('specifications')}
                  placeholder="Dimensions, ratings, tolerances..." />
              </Grid>
            </Grid>

            {/* Pricing */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AttachMoney sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary">PRICING & QUANTITY</Typography>
            </Box>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth label="Quantity" type="number" required
                  value={formData.quantity} onChange={handleChange('quantity')} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth label="Min Order Qty" type="number"
                  value={formData.min_order_quantity} onChange={handleChange('min_order_quantity')} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth label="Original Price" type="number"
                  value={formData.original_price} onChange={handleChange('original_price')}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth label="Selling Price" type="number" required
                  value={formData.selling_price} onChange={handleChange('selling_price')}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Condition</InputLabel>
                  <Select value={formData.condition} label="Condition" onChange={handleChange('condition')}>
                    <MenuItem value="new">New (Unused)</MenuItem>
                    <MenuItem value="like_new">Like New</MenuItem>
                    <MenuItem value="good">Good</MenuItem>
                    <MenuItem value="fair">Fair</MenuItem>
                    <MenuItem value="poor">Poor</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth label="Warranty (months)" type="number"
                  value={formData.warranty_months} onChange={handleChange('warranty_months')} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth label="Weight (kg)" type="number"
                  value={formData.weight_kg} onChange={handleChange('weight_kg')} />
              </Grid>
            </Grid>

            {/* Location */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocationOn sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary">LOCATION & DATE</Typography>
            </Box>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Location / City" value={formData.location}
                  onChange={handleChange('location')} placeholder="e.g., Chennai, Mumbai" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Manufacturing Date" type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.manufacturing_date} onChange={handleChange('manufacturing_date')} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* AI Suggestion */}
            <Box sx={{
              p: 3, borderRadius: 3,
              background: 'linear-gradient(135deg, #eff6ff 0%, #faf5ff 100%)',
              border: '1px solid #dbeafe',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: '#dbeafe', color: '#2563eb', width: 36, height: 36 }}>
                    <AutoAwesome sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>AI Price Suggestion</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Get an AI-recommended selling price
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained" onClick={handleAnalyze} disabled={analyzing}
                  startIcon={<AutoAwesome />} size="small"
                  sx={{ background: 'linear-gradient(135deg, #7c3aed, #2563eb)' }}
                >
                  {analyzing ? 'Analyzing...' : 'Get Suggestion'}
                </Button>
              </Box>
              {aiSuggestion && (
                <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }} icon={<CheckCircle />}>
                  <Typography variant="body2" fontWeight={500}>
                    {aiSuggestion.recommendation || `Suggested: ₹${aiSuggestion.suggested_price} | Demand: ${aiSuggestion.demand}`}
                  </Typography>
                </Alert>
              )}
            </Box>

            <Button
              fullWidth variant="contained" size="large" type="submit"
              disabled={loading} startIcon={<CloudUpload />} endIcon={<ArrowForward />}
              sx={{ mt: 4, py: 1.8 }}
            >
              {loading ? 'Publishing...' : 'Publish Listing'}
            </Button>
          </Box>
        </MotionPaper>
      </Container>
    </Box>
  );
};

export default UploadInventoryPage;

